import {
  Anchor,
  ArrowUpRight,
  RotateCcw,
  Trash2,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BasketMinInvestmentResult,
  BasketMinInvestmentStock,
  computeBasketMinInvestment,
} from "../../../../utils/basketMinInvestment";
import {
  formatCurrency,
  formatNumberFixed,
} from "../../../../utils/numberUtils";

// ---------------------------------------------------------------------------
// Public input type -- the provider adapter and admin hook both produce this.
// ---------------------------------------------------------------------------

export interface MinInvestmentEntry {
  conId: number;
  ticker: string;
  name: string;
  weight: number;
  minSize: number | null;
  price: number | null;
  nativePrice: number | null;
  currency: string;
  fxRate: number | null;
  isOption: boolean;
  multiplier: number;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatMoney(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "--";
  const abs = Math.abs(v);
  const fmt = (num: number, decimals: number) =>
    num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  if (abs >= 1e9) return `$${fmt(v / 1e9, 2)}B`;
  if (abs >= 1e6) return `$${fmt(v / 1e6, 2)}M`;
  if (abs >= 1e3) return `$${fmt(v / 1e3, 1)}K`;
  return `$${fmt(v, 0)}`;
}

function formatMoneyExact(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "--";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export interface MinInvestmentAnalysisProps {
  entries: MinInvestmentEntry[];
}

export function entriesToScenario(
  entries: MinInvestmentEntry[]
): BasketMinInvestmentStock[] {
  return entries.map((e) => ({
    conId: e.conId,
    ticker: e.ticker,
    name: e.name,
    weight: e.weight,
    minSize: e.minSize ?? 0,
    price: e.price ?? 0,
    enabled: true,
  }));
}

interface ContractMeta {
  currency: string;
  nativePrice: number | null;
  fxRate: number | null;
}

const MinInvestmentAnalysis: React.FC<MinInvestmentAnalysisProps> = ({ entries }) => {
  const baseline = useMemo(() => {
    const raw = entriesToScenario(entries);
    const initialResult = computeBasketMinInvestment(raw);
    const ratioByConId = new Map<number, number>();
    initialResult?.perContract.forEach((c) =>
      ratioByConId.set(c.conId, c.ratio)
    );
    return raw
      .map((s, idx) => ({ s, idx }))
      .sort((a, b) => {
        const ra = ratioByConId.get(a.s.conId);
        const rb = ratioByConId.get(b.s.conId);
        if (ra != null && rb == null) return -1;
        if (ra == null && rb != null) return 1;
        if (ra != null && rb != null && rb !== ra) return rb - ra;
        return a.idx - b.idx;
      })
      .map(({ s }) => s);
  }, [entries]);

  const metaByConId = useMemo(() => {
    const map = new Map<number, ContractMeta>();
    for (const e of entries) {
      map.set(e.conId, {
        currency: e.currency,
        nativePrice: e.nativePrice,
        fxRate: e.fxRate,
      });
    }
    return map;
  }, [entries]);

  const [scenario, setScenario] = useState<BasketMinInvestmentStock[]>(baseline);

  useEffect(() => {
    setScenario(baseline);
  }, [baseline]);

  const updateWeight = useCallback((conId: number, nextAbsWeight: number) => {
    setScenario((prev) =>
      prev.map((s) => {
        if (s.conId !== conId) return s;
        const sign = s.weight < 0 ? -1 : 1;
        const clamped = Math.max(0, Math.min(100, nextAbsWeight));
        return { ...s, weight: sign * clamped };
      })
    );
  }, []);

  const toggleEnabled = useCallback((conId: number) => {
    setScenario((prev) =>
      prev.map((s) =>
        s.conId === conId ? { ...s, enabled: s.enabled === false } : s
      )
    );
  }, []);

  const resetScenario = useCallback(() => {
    setScenario(baseline);
  }, [baseline]);

  const result = useMemo(
    () => computeBasketMinInvestment(scenario),
    [scenario]
  );
  const baselineResult = useMemo(
    () => computeBasketMinInvestment(baseline),
    [baseline]
  );

  const totalAbsWeightPct = scenario
    .filter((s) => s.enabled !== false)
    .reduce((acc, s) => acc + Math.abs(s.weight), 0);
  const cashWeightPct = 100 - totalAbsWeightPct;

  return (
    <div className="space-y-3">
      <HeroStats result={result} baselineResult={baselineResult} />

      {result && <BindingBanner result={result} />}

      {!result && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          No active contracts with valid lot size and price. Toggle contracts
          on or check that contract metadata is populated.
        </div>
      )}

      {result && (
        <InsightStrip
          scenario={scenario}
          result={result}
          onApplyScenario={setScenario}
        />
      )}

      <CompositionTable
        scenario={scenario}
        metaByConId={metaByConId}
        result={result}
        totalAbsWeightPct={totalAbsWeightPct}
        cashWeightPct={cashWeightPct}
        onUpdateWeight={updateWeight}
        onToggleEnabled={toggleEnabled}
        onReset={resetScenario}
      />
    </div>
  );
};

export default MinInvestmentAnalysis;

// ---------------------------------------------------------------------------
// Compute headline number without rendering the full UI.
// Used by ExposureTiles to display the min investment as a tile value.
// ---------------------------------------------------------------------------

export function computeMinInvestmentHeadline(
  entries: MinInvestmentEntry[]
): number | null {
  if (entries.length === 0) return null;
  const hasUnpricedRow = entries.some(
    (e) => e.weight !== 0 && e.price == null
  );
  if (hasUnpricedRow) return null;
  const stocks = entriesToScenario(entries);
  const result = computeBasketMinInvestment(stocks);
  return result?.theoreticalMin ?? null;
}

// ---------------------------------------------------------------------------
// Hero stats
// ---------------------------------------------------------------------------

const HeroStats: React.FC<{
  result: BasketMinInvestmentResult | null;
  baselineResult: BasketMinInvestmentResult | null;
}> = ({ result, baselineResult }) => {
  const baselineMin = baselineResult?.theoreticalMin ?? null;
  const deltaVsBaseline =
    result && baselineMin
      ? (result.theoreticalMin - baselineMin) / baselineMin
      : null;

  const binding = result?.bindingContract;
  const caption = binding ? (
    <>
      Binding:{" "}
      <span className="font-mono font-semibold text-gray-700 dark:text-gray-200">
        {binding.ticker}
      </span>{" "}
      &middot; lot {formatMoney(binding.lotTicket)} / weight{" "}
      {formatNumberFixed(Math.abs(binding.weight), 2)}%
      {deltaVsBaseline != null && Math.abs(deltaVsBaseline) > 1e-6 && (
        <span
          className={`ml-2 font-mono ${deltaVsBaseline < 0 ? "text-green-600 dark:text-green-500" : "text-red-600 dark:text-red-500"}`}
        >
          {deltaVsBaseline < 0 ? "-" : "+"}
          {formatNumberFixed(Math.abs(deltaVsBaseline * 100), 0)}% vs active
        </span>
      )}
    </>
  ) : (
    "No active contracts"
  );

  return (
    <div className="bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-lg">
      <div className="px-4 py-3 md:px-5 md:py-4">
        <div className="flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-rp-amber" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Min investment
          </span>
        </div>
        <div className="mt-1.5 font-mono font-semibold break-words text-2xl text-gray-900 dark:text-white">
          {formatMoney(result?.theoreticalMin)}
        </div>
        <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
          {caption}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Binding contract banner
// ---------------------------------------------------------------------------

const BindingBanner: React.FC<{ result: BasketMinInvestmentResult }> = ({ result }) => {
  const b = result.bindingContract;
  return (
    <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400 leading-snug">
      <Anchor className="w-3 h-3 mt-0.5 text-rp-amber flex-shrink-0" />
      <span>
        Binding:{" "}
        <span className="font-mono font-semibold text-gray-900 dark:text-white">
          {b.ticker}
        </span>{" "}
        at {formatNumberFixed(Math.abs(b.weight), 1)}% &middot;{" "}
        <span className="font-mono" title={formatMoneyExact(b.lotTicket)}>
          {formatMoney(b.lotTicket)}
        </span>{" "}
        lot ticket. Bump its weight, drop it, or swap to a smaller-lot
        equivalent to move the min.
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Insight strip
// ---------------------------------------------------------------------------

const InsightStrip: React.FC<{
  scenario: BasketMinInvestmentStock[];
  result: BasketMinInvestmentResult;
  onApplyScenario: (next: BasketMinInvestmentStock[]) => void;
}> = ({ scenario, result, onApplyScenario }) => {
  const binding = result.bindingContract;

  const dropScenario = scenario.map((s) =>
    s.conId === binding.conId ? { ...s, enabled: false } : s
  );
  const dropResult = computeBasketMinInvestment(dropScenario);

  const bumpScenario = scenario.map((s) =>
    s.conId === binding.conId
      ? {
          ...s,
          weight: (s.weight < 0 ? -1 : 1) * Math.min(100, Math.abs(s.weight) * 2),
        }
      : s
  );
  const bumpResult = computeBasketMinInvestment(bumpScenario);

  type ChipSpec = {
    icon: React.ReactNode;
    label: string;
    newMin: number;
    apply: () => void;
  };
  const chips: ChipSpec[] = [];

  if (dropResult) {
    chips.push({
      icon: <Trash2 className="w-3 h-3" />,
      label: `Drop ${binding.ticker}`,
      newMin: dropResult.theoreticalMin,
      apply: () => onApplyScenario(dropScenario),
    });
  }
  if (bumpResult && Math.abs(binding.weight) < 100) {
    chips.push({
      icon: <ArrowUpRight className="w-3 h-3" />,
      label: `Double ${binding.ticker}`,
      newMin: bumpResult.theoreticalMin,
      apply: () => onApplyScenario(bumpScenario),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <span className="text-gray-500 dark:text-gray-400">Try:</span>
      {chips.map((c) => {
        const delta = c.newMin - result.theoreticalMin;
        const deltaPct =
          result.theoreticalMin > 0 ? delta / result.theoreticalMin : 0;
        const better = delta < 0;
        return (
          <button
            key={c.label}
            type="button"
            onClick={c.apply}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator hover:border-rp-amber rounded-md transition-colors"
          >
            <span className="text-gray-500 dark:text-gray-400">{c.icon}</span>
            <span className="text-gray-700 dark:text-gray-200">{c.label}</span>
            <span className="font-mono text-gray-500 dark:text-gray-400">
              {formatMoney(c.newMin)}
            </span>
            <span
              className={`font-mono ${better ? "text-green-600 dark:text-green-500" : "text-red-600 dark:text-red-500"}`}
            >
              {better ? "-" : "+"}
              {formatNumberFixed(Math.abs(deltaPct * 100), 0)}%
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Composition table
// ---------------------------------------------------------------------------

type RowBreakdown = BasketMinInvestmentResult["perContract"][number];

interface RowState {
  breakdown: RowBreakdown | undefined;
  meta: ContractMeta | undefined;
  isBinding: boolean;
  isInvalid: boolean;
  disabledRow: boolean;
  isInconsequential: boolean;
  nonUsd: boolean;
  fxTooltip: string | undefined;
  allocated: number | null;
  lotTicket: number | null;
  allocatedTooltip: string | undefined;
}

function getRowState(
  s: BasketMinInvestmentStock,
  result: BasketMinInvestmentResult | null,
  resultByConId: Map<number, RowBreakdown>,
  metaByConId: Map<number, ContractMeta>,
  bindingId: number | undefined
): RowState {
  const breakdown = resultByConId.get(s.conId);
  const meta = metaByConId.get(s.conId);
  const isBinding = bindingId === s.conId && s.enabled !== false;
  const isInvalid = s.minSize <= 0 || s.price <= 0 || s.weight === 0;
  const disabledRow = s.enabled === false || isInvalid;
  const isInconsequential =
    !disabledRow && !isBinding && s.minSize > 0 && s.minSize < 1;
  const nonUsd = !!meta && meta.currency !== "USD";
  const fxTooltip =
    nonUsd && meta && meta.nativePrice != null && meta.fxRate != null
      ? `${meta.currency} ${meta.nativePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} @ ${formatNumberFixed(meta.fxRate, 4)} \u2192 USD`
      : nonUsd && meta
        ? `${meta.currency} price -- FX rate unavailable`
        : undefined;
  const minInvestment = result?.theoreticalMin ?? 0;
  const allocated =
    !disabledRow && minInvestment > 0
      ? (Math.abs(s.weight) / 100) * minInvestment
      : null;
  const lotTicket = s.minSize > 0 && s.price > 0 ? s.minSize * s.price : null;
  const allocatedTooltip =
    allocated != null && lotTicket != null
      ? `${formatMoneyExact(allocated)} allocated / ${formatMoneyExact(lotTicket)} lot ticket = ${formatNumberFixed(allocated / lotTicket, 2)} lots of slack`
      : undefined;
  return {
    breakdown, meta, isBinding, isInvalid, disabledRow, isInconsequential,
    nonUsd, fxTooltip, allocated, lotTicket, allocatedTooltip,
  };
}

const TickerBadges: React.FC<{
  s: BasketMinInvestmentStock;
  state: RowState;
}> = ({ s, state }) => (
  <>
    {s.weight < 0 && (
      <span
        className="text-[10px] font-semibold text-red-600 dark:text-red-400 tracking-wider flex-shrink-0"
        title="Short position"
      >
        SHORT
      </span>
    )}
    {state.nonUsd && state.meta && (
      <span
        className="text-[10px] font-medium text-blue-600 dark:text-blue-400 tracking-wider flex-shrink-0"
        title={state.fxTooltip}
      >
        {state.meta.currency}
      </span>
    )}
    {state.isInvalid && (
      <span
        className="text-[10px] font-medium text-amber-600 dark:text-amber-500 flex-shrink-0"
        title={
          state.nonUsd && state.meta?.fxRate == null
            ? "FX rate unavailable; excluded"
            : "Missing lot size or price; excluded"
        }
      >
        n/a
      </span>
    )}
  </>
);

const CompositionTable: React.FC<{
  scenario: BasketMinInvestmentStock[];
  metaByConId: Map<number, ContractMeta>;
  result: BasketMinInvestmentResult | null;
  totalAbsWeightPct: number;
  cashWeightPct: number;
  onUpdateWeight: (conId: number, nextAbsWeight: number) => void;
  onToggleEnabled: (conId: number) => void;
  onReset: () => void;
}> = ({
  scenario, metaByConId, result, totalAbsWeightPct, cashWeightPct,
  onUpdateWeight, onToggleEnabled, onReset,
}) => {
  const bindingId = result?.bindingContract.conId;
  const resultByConId = useMemo(() => {
    const map = new Map<number, RowBreakdown>();
    result?.perContract.forEach((r) => map.set(r.conId, r));
    return map;
  }, [result]);

  return (
    <section className="bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-lg overflow-hidden">
      <div className="px-3 md:px-4 py-2.5 border-b border-gray-200 dark:border-rp-separator flex items-center gap-4 md:gap-6 text-xs flex-wrap">
        <div className="flex items-center gap-3 md:gap-4">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Weights</span>
            <span className="ml-1.5 font-mono font-semibold">
              {formatNumberFixed(totalAbsWeightPct, 1)}%
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Cash</span>
            <span
              className={`ml-1.5 font-mono font-semibold ${cashWeightPct < 0 ? "text-red-600 dark:text-red-500" : ""}`}
            >
              {formatNumberFixed(cashWeightPct, 1)}%
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="ml-auto flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 text-xs rounded-md transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          Reset
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-8" />
            <col />
            <col className="w-[200px]" />
            <col className="w-[70px]" />
            <col className="w-[80px]" />
            <col className="w-[100px]" />
            <col className="w-[100px]" />
            <col className="w-[120px]" />
          </colgroup>
          <thead className="bg-gray-50 dark:bg-rp-surface-sunken text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <tr>
              <th className="text-left pl-4 pr-1 py-2"></th>
              <th className="text-left px-2 py-2">Ticker</th>
              <th className="text-left px-2 py-2">Weight</th>
              <th className="text-right px-2 py-2">Lot</th>
              <th className="text-right px-2 py-2">Price</th>
              <th className="text-right px-2 py-2">Lot ticket</th>
              <th className="text-right px-2 py-2">M / w</th>
              <th
                className="text-right px-2 pr-4 py-2"
                title="Dollars allocated to this contract at the Min investment (weight x Min investment). Should always be >= Lot ticket."
              >
                $ allocated
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-rp-separator">
            {scenario.map((s) => {
              const state = getRowState(s, result, resultByConId, metaByConId, bindingId);
              const { breakdown, isBinding, disabledRow, isInconsequential, fxTooltip, allocated, allocatedTooltip } = state;
              return (
                <tr
                  key={s.conId}
                  className={`group transition-colors ${
                    disabledRow
                      ? "opacity-40"
                      : isInconsequential
                        ? "opacity-50 hover:opacity-100 hover:bg-gray-50 dark:hover:bg-rp-surface-raised"
                        : "hover:bg-gray-50 dark:hover:bg-rp-surface-raised"
                  }`}
                >
                  <td className="pl-4 pr-1 py-2 relative">
                    {isBinding && <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-rp-amber" />}
                    <input
                      type="checkbox"
                      checked={s.enabled !== false}
                      onChange={() => onToggleEnabled(s.conId)}
                      className="w-3.5 h-3.5 rounded accent-rp-amber cursor-pointer"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isBinding && <span className="w-1 h-1 rounded-full bg-rp-amber flex-shrink-0" title="Binding contract" />}
                      <span className="font-mono font-semibold text-gray-900 dark:text-white truncate">{s.ticker}</span>
                      <TickerBadges s={s} state={state} />
                      {s.name && <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate min-w-0">&middot; {s.name}</span>}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="range" min={0} max={100} step={0.1}
                        value={Math.abs(s.weight)}
                        onChange={(e) => onUpdateWeight(s.conId, Number(e.target.value))}
                        disabled={disabledRow}
                        className="flex-1 min-w-0 accent-rp-amber"
                      />
                      <EditableWeight value={Math.abs(s.weight)} disabled={disabledRow} onCommit={(next) => onUpdateWeight(s.conId, next)} />
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-xs text-gray-600 dark:text-gray-300">
                    {s.minSize > 0 ? s.minSize : "--"}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-xs text-gray-600 dark:text-gray-300" title={fxTooltip}>
                    {s.price > 0 ? formatCurrency(s.price, 2) : "--"}
                  </td>
                  <td
                    className="px-2 py-2 text-right font-mono text-xs text-gray-700 dark:text-gray-200"
                    title={s.minSize > 0 && s.price > 0 ? formatMoneyExact(s.minSize * s.price) : undefined}
                  >
                    {s.minSize > 0 && s.price > 0 ? formatMoney(s.minSize * s.price) : "--"}
                  </td>
                  <td
                    className={`px-2 py-2 text-right font-mono text-xs ${isBinding ? "text-rp-amber font-semibold" : "text-gray-700 dark:text-gray-200"}`}
                    title={breakdown ? formatMoneyExact(breakdown.ratio) : undefined}
                  >
                    {breakdown ? formatMoney(breakdown.ratio) : "--"}
                  </td>
                  <td
                    className={`px-2 pr-4 py-2 text-right font-mono text-xs ${isBinding ? "text-rp-amber font-semibold" : "text-gray-700 dark:text-gray-200"}`}
                    title={allocatedTooltip}
                  >
                    {allocated != null ? formatMoney(allocated) : "--"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden divide-y divide-gray-100 dark:divide-rp-separator">
        {scenario.map((s) => {
          const state = getRowState(s, result, resultByConId, metaByConId, bindingId);
          const { breakdown, isBinding, disabledRow, isInconsequential, allocated, lotTicket, allocatedTooltip } = state;
          return (
            <div
              key={s.conId}
              className={`relative px-3 py-3 ${disabledRow ? "opacity-50" : isInconsequential ? "opacity-60" : ""}`}
            >
              {isBinding && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-rp-amber" />}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <input
                    type="checkbox"
                    checked={s.enabled !== false}
                    onChange={() => onToggleEnabled(s.conId)}
                    className="w-4 h-4 mt-0.5 rounded accent-rp-amber cursor-pointer flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                      {isBinding && <span className="w-1.5 h-1.5 rounded-full bg-rp-amber flex-shrink-0" title="Binding contract" />}
                      <span className="font-mono font-semibold text-sm text-gray-900 dark:text-white break-all">{s.ticker}</span>
                      <TickerBadges s={s} state={state} />
                    </div>
                    {s.name && <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">{s.name}</div>}
                  </div>
                </div>
                <EditableWeight value={Math.abs(s.weight)} disabled={disabledRow} onCommit={(next) => onUpdateWeight(s.conId, next)} />
              </div>
              <input
                type="range" min={0} max={100} step={0.1}
                value={Math.abs(s.weight)}
                onChange={(e) => onUpdateWeight(s.conId, Number(e.target.value))}
                disabled={disabledRow}
                className="w-full mt-3 accent-rp-amber"
              />
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                <MobileMetric label="$ alloc" value={allocated != null ? formatMoney(allocated) : "--"} highlight={isBinding} title={allocatedTooltip} />
                <MobileMetric label="Lot ticket" value={lotTicket != null ? formatMoney(lotTicket) : "--"} title={lotTicket != null ? formatMoneyExact(lotTicket) : undefined} />
                <MobileMetric label="M / w" value={breakdown ? formatMoney(breakdown.ratio) : "--"} highlight={isBinding} title={breakdown ? formatMoneyExact(breakdown.ratio) : undefined} />
                <MobileMetric
                  label="Lot x Price"
                  value={s.minSize > 0 && s.price > 0 ? `${s.minSize} x ${formatCurrency(s.price, 2)}` : "--"}
                />
              </dl>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const MobileMetric: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
  title?: string;
}> = ({ label, value, highlight, title }) => (
  <div className="flex items-baseline justify-between gap-2" title={title}>
    <dt className="text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wider truncate">
      {label}
    </dt>
    <dd className={`tabular-nums truncate text-right ${highlight ? "text-rp-amber font-semibold" : "text-gray-700 dark:text-gray-200"}`}>
      {value}
    </dd>
  </div>
);

// ---------------------------------------------------------------------------
// Editable weight
// ---------------------------------------------------------------------------

const EditableWeight: React.FC<{
  value: number;
  disabled?: boolean;
  onCommit: (next: number) => void;
}> = ({ value, disabled, onCommit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEdit = useCallback(() => {
    if (disabled) return;
    setDraft(formatNumberFixed(value, 1));
    setEditing(true);
  }, [disabled, value]);

  const commit = useCallback(() => {
    const parsed = Number(draft.replace(/[,%\s]/g, ""));
    if (Number.isFinite(parsed)) {
      onCommit(Math.max(0, Math.min(100, parsed)));
    }
    setEditing(false);
  }, [draft, onCommit]);

  const cancel = useCallback(() => setEditing(false), []);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        className="font-mono text-xs font-semibold w-14 text-right bg-white dark:bg-rp-surface-raised border border-rp-amber rounded px-1 py-0.5 outline-none text-gray-900 dark:text-white"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      disabled={disabled}
      className={`font-mono text-xs font-semibold w-14 text-right select-none tabular-nums px-1 py-0.5 rounded border border-transparent text-gray-900 dark:text-white ${
        disabled
          ? "cursor-not-allowed opacity-60"
          : "cursor-text hover:border-gray-200 dark:hover:border-rp-separator active:border-rp-amber"
      }`}
      title={disabled ? undefined : "Tap to type a value"}
    >
      {formatNumberFixed(value, 1)}%
    </button>
  );
};
